import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ResponsivePie } from '@nivo/pie';
import { useTheme } from '@mui/material/styles';
import nivoTheme from '../../theme/nivoTheme';

/**
 * Status-Donut (Charts 2.0, Alexanders Entscheidung: Donut bleibt —
 * aber mit Kennzahlen): Zentrum trägt Gesamtwert + Label, die Legende
 * ist eine Wertetabelle (Farbe · Label · Wert) statt bloßer
 * Farbzuordnung; der Anteil in Prozent lebt im Hover-Tooltip.
 *
 *   data        – [{ id, label, value, color }] (Nullwerte vorher filtern)
 *   centerLabel – kleines Wort unter der Gesamtzahl (z. B. „Leads")
 *   onSliceClick(id) – optional: Klick auf Segment oder Legendenzeile
 */
export default function StatusDonut({ data, centerLabel, onSliceClick }) {
  const theme = useTheme();
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const pct = (v) => (total ? Math.round((v / total) * 100) : 0);

  // Zahl im Segment: Tinte auf hellen Ramp-Stufen, Weiß auf dunklen —
  // nach Luminanz der Segmentfarbe (Regel: Label im Fill wählt nach Helligkeit)
  const arcLabelColor = (d) => {
    const hex = String(d.color || '');
    if (!hex.startsWith('#') || hex.length < 7) return theme.palette.common.white;
    const lum = 0.299 * parseInt(hex.slice(1, 3), 16)
      + 0.587 * parseInt(hex.slice(3, 5), 16)
      + 0.114 * parseInt(hex.slice(5, 7), 16);
    return lum > 150 ? '#14181F' : '#FFFFFF';
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minHeight: 0 }}>
      <Box sx={{ width: '42%', minWidth: 96, height: '100%', position: 'relative' }}>
        <ResponsivePie
          theme={nivoTheme(theme)}
          data={data}
          margin={{ top: 6, right: 6, bottom: 6, left: 6 }}
          innerRadius={0.58}
          padAngle={1.2}
          cornerRadius={3}
          colors={{ datum: 'data.color' }}
          enableArcLabels
          arcLabel={(d) => String(d.value)}
          arcLabelsSkipAngle={30}
          arcLabelsTextColor={arcLabelColor}
          enableArcLinkLabels={false}
          onClick={onSliceClick ? (d) => onSliceClick(d.id) : undefined}
          layers={[
            'arcs', 'arcLabels',
            ({ centerX, centerY }) => (
              <g>
                <text x={centerX} y={centerY - (centerLabel ? 5 : 0)} textAnchor="middle" dominantBaseline="central"
                  style={{ fontSize: 19, fontWeight: 700, fill: theme.palette.text.primary, fontVariantNumeric: 'tabular-nums' }}>
                  {total}
                </text>
                {centerLabel && (
                  <text x={centerX} y={centerY + 13} textAnchor="middle" dominantBaseline="central"
                    style={{ fontSize: 9.5, fontWeight: 550, fill: theme.palette.text.secondary }}>
                    {centerLabel}
                  </text>
                )}
              </g>
            ),
          ]}
          tooltip={({ datum }) => (
            <Box sx={{ bgcolor: 'background.paper', px: 1.25, py: 0.75, borderRadius: 1.5, border: 1, borderColor: 'divider', fontSize: '0.8rem', boxShadow: 2 }}>
              <strong>{datum.label}</strong>: {datum.value} · {pct(datum.value)}%
            </Box>
          )}
        />
      </Box>

      {/* Werte-Legende: jede Zeile beantwortet „wie viele?" direkt */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.4 }}>
        {data.map((d) => (
          <Box key={d.id} onClick={onSliceClick ? () => onSliceClick(d.id) : undefined}
            sx={{
              display: 'grid', gridTemplateColumns: '10px 1fr auto', alignItems: 'center', gap: 0.9,
              fontSize: '0.76rem', lineHeight: 1.35, cursor: onSliceClick ? 'pointer' : 'default',
              borderRadius: 1, px: 0.5, mx: -0.5,
              '&:hover': onSliceClick ? { bgcolor: 'action.hover' } : {},
            }}>
            <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: d.color, flex: 'none' }} />
            <Typography component="span" noWrap sx={{ fontSize: 'inherit', color: 'text.secondary', fontWeight: 550, minWidth: 0 }}>
              {d.label}
            </Typography>
            <Typography component="span" sx={{ fontSize: 'inherit', color: 'text.primary', fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>
              {d.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
